import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';

// Playbook §6.3: count every cart change so a late response never repaints over a newer state.
let cartMutationSeq = 0;
document.addEventListener(ThemeEvents.cartUpdate, () => { cartMutationSeq++; });

/**
 * Shipping Protection toggle in the cart drawer (Horizon 3.3 port of the SCENTREAT component).
 *
 * The section is morphed in place after every cart change, so the data attributes update and
 * `#reconcile` runs: on first render the line is added (default on) unless the shopper switched
 * it off earlier in this session; the switch adds/removes the line through /cart/update.js and
 * asks for the drawer section back. The theme's <cart-items-component> listens for cart:update
 * and morphs `detail.data.sections[sectionId]` itself, so the drawer refreshes in one round trip.
 */
class ShippingProtection extends Component {
  #busy = false;
  #queued = false;
  #attempts = 0;
  static observedAttributes = ['data-desired-variant', 'data-current-variant', 'data-line-key'];

  connectedCallback() {
    super.connectedCallback();
    this.#schedule();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback?.(name, oldValue, newValue);
    if (oldValue === newValue) return;
    this.#schedule();
  }

  #schedule() {
    if (this.#queued) return;
    this.#queued = true;
    const run = () => {
      if (!this.#queued) return;
      this.#queued = false;
      if (this.isConnected) this.#reconcile();
    };
    requestAnimationFrame(run);
    setTimeout(run, 150);
  }

  get #desired() {
    return this.dataset.desiredVariant || '';
  }

  get #current() {
    const v = this.dataset.currentVariant;
    return v && v !== '0' ? v : '';
  }

  get #declined() {
    try { return sessionStorage.getItem('st-protection-declined') === '1'; } catch { return false; }
  }

  set #declined(v) {
    try { v ? sessionStorage.setItem('st-protection-declined', '1') : sessionStorage.removeItem('st-protection-declined'); } catch {}
  }

  #reconcile() {
    if (this.#busy || this.#attempts >= 4) return;
    if (!this.#current) {
      if (this.#desired && this.dataset.defaultOn === '1' && !this.#declined) this.#apply({ [this.#desired]: 1 }, 'add');
      return;
    }
    if (!this.#desired) this.#apply({ [this.#current]: 0 }, 'remove');
  }

  toggle = () => {
    if (this.#busy || !this.refs.toggle) return;
    if (this.refs.toggle.checked) {
      this.#declined = false;
      if (this.#desired) this.#apply({ [this.#desired]: 1 }, 'add');
    } else if (this.#current) {
      this.#declined = true;
      this.#apply({ [this.#current]: 0 }, 'remove');
    }
  };

  async #apply(updates, intent) {
    this.#busy = true;
    this.#attempts++;
    this.setAttribute('loading', '');
    const sectionId = this.dataset.sectionId || '';
    if (this.refs.toggle) this.refs.toggle.checked = intent !== 'remove';
    this.dataset.qty = intent === 'remove' ? '0' : '1';

    try {
      const body = JSON.stringify({ updates, ...(sectionId ? { sections: [sectionId], sections_url: window.location.pathname } : {}) });
      const seqAtStart = cartMutationSeq;
      const response = await fetch(Theme.routes.cart_update_url, fetchConfig('json', { body }));
      const data = await response.json();
      if (!response.ok) throw new Error(data?.description || data?.message || 'Cart update failed');
      // a newer cart change started meanwhile: announce the cart, but let that change paint the drawer
      if (cartMutationSeq !== seqAtStart) data.sections = undefined;

      // cart-items-component morphs the drawer from `sections`; cart-icon updates its count.
      this.dispatchEvent(
        new CartUpdateEvent(data, this.id || 'st-shipping-protection', {
          source: 'st-shipping-protection',
          itemCount: data.item_count,
          sections: data.sections,
        })
      );
    } catch (error) {
      if (this.refs.toggle) this.refs.toggle.checked = Boolean(this.#current);
      console.warn('[st-shipping-protection]', error);
    } finally {
      this.#busy = false;
      this.removeAttribute('loading');
    }
  }
}

if (!customElements.get('st-shipping-protection')) customElements.define('st-shipping-protection', ShippingProtection);
